import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Switch, Typography,
  alpha, useMediaQuery, useTheme,
} from '@mui/material';
import { AddRounded, Close, DeleteOutline, InfoOutlined, WarningAmberRounded } from '@mui/icons-material';

import useDialogStyles from '../hooks/useDialogStyles';

// Shared pieces for the "Add …" buttons and dialogs (Users, Units, SIF Codes, Applications, VPN),
// so every one of them looks like the redesigned Add User dialog.

// Gold add button, same 36px height and corners as the search boxes beside it.
// shortLabel is used on phones (and is the default label).
export const AddButton = ({ label = 'Add', shortLabel = 'Add', icon = <AddRounded />, onClick, disabled, sx }) => {
  const theme = useTheme();
  const { goldButtonSx } = useDialogStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const accent = theme.palette.secondary.main;

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      startIcon={icon}
      sx={{
        ...goldButtonSx,
        height: 36,
        px: 2,
        borderRadius: 2,
        textTransform: 'none',
        fontSize: '0.875rem',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&.Mui-disabled': {
          backgroundColor: alpha(accent, 0.25),
          color: alpha(theme.palette.getContrastText(accent), 0.6),
        },
        ...sx,
      }}
    >
      {isMobile ? shortLabel : label}
    </Button>
  );
};

// Dialog title: round gold icon, title + one-line subtitle, close button
export const DialogHeader = ({ icon: Icon, title, subtitle, onClose, disabled }) => {
  const theme = useTheme();
  const { titleSx } = useDialogStyles();
  const accent = theme.palette.secondary.main;

  return (
    <Box sx={{ ...titleSx, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {Icon && (
        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            color: accent,
            backgroundColor: alpha(accent, 0.12),
            border: `1px solid ${alpha(accent, 0.4)}`,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>
      )}
      <Box flex={1} minWidth={0}>
        <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{subtitle}</Typography>
        )}
      </Box>
      {onClose && (
        <IconButton onClick={onClose} disabled={disabled} aria-label="Close" sx={{ color: theme.palette.text.secondary }}>
          <Close fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

// Small section heading inside a form grid (spans every column)
export const FormSection = ({ icon: Icon, title, hint }) => {
  const theme = useTheme();
  const accent = theme.palette.secondary.main;

  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ gridColumn: '1 / -1', mt: 0.5 }}>
      {Icon && <Icon sx={{ fontSize: 18, color: accent }} />}
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
        {title}
      </Typography>
      {hint && <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>· {hint}</Typography>}
      <Box flex={1} sx={{ borderTop: `1px solid ${theme.palette.divider}`, ml: 1 }} />
    </Box>
  );
};

// On/off field with a line saying what the current state means; same frame as a small text field
export const SwitchField = ({ label, checked, onChange, onText, offText, sx }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      sx={{
        px: 1.5,
        minHeight: 40,
        borderRadius: 1.5,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.28)' : theme.palette.neutral[300]}`,
        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : theme.palette.background.default,
        ...sx,
      }}
    >
      <Box minWidth={0}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2 }}>{label}</Typography>
        {(onText || offText) && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {checked ? onText : offText}
          </Typography>
        )}
      </Box>
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        inputProps={{ 'aria-label': label }}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: accent },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accent },
        }}
      />
    </Box>
  );
};

// Gold submit button with a spinner and "busy" text while saving
export const SubmitButton = ({ label, busyLabel = 'Saving…', busy, onClick, icon, disabled }) => {
  const theme = useTheme();
  const { goldButtonSx } = useDialogStyles();
  const accent = theme.palette.secondary.main;

  return (
    <Button
      variant="contained"
      onClick={onClick}
      disabled={busy || disabled}
      startIcon={busy ? <CircularProgress size={16} color="inherit" /> : icon}
      sx={{
        ...goldButtonSx,
        textTransform: 'none',
        '&.Mui-disabled': { backgroundColor: alpha(accent, 0.5), color: theme.palette.getContrastText(accent) },
      }}
    >
      {busy ? busyLabel : label}
    </Button>
  );
};

// Red action button (delete, leave, …) with the same shape as the gold one
export const useDangerButtonSx = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    fontWeight: 700,
    borderRadius: 1.5,
    px: 2.5,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': { backgroundColor: theme.palette.error.dark, boxShadow: 'none' },
    '&.Mui-disabled': {
      backgroundColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
      color: theme.palette.text.disabled,
    },
  };
};

/**
 * Confirmation for destructive actions, used by every "Delete …" dialog.
 *
 * title        "Delete SIF code?"
 * message      what will happen (text or nodes)
 * item         name of the thing being deleted, shown in a card
 * itemDetail   one extra line under the name (optional)
 * itemIcon     icon in the card (optional)
 * note         small line at the bottom; defaults to "This can't be undone." (pass null to hide)
 * busy/error   request state; while busy the dialog can't be closed
 */
export const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Delete?',
  message,
  item,
  itemDetail,
  itemIcon: ItemIcon,
  note = "This can't be undone.",
  confirmLabel = 'Delete',
  busyLabel = 'Deleting…',
  confirmIcon = <DeleteOutline />,
  busy = false,
  error,
}) => {
  const theme = useTheme();
  const { paperSx, actionsSx, cancelButtonSx } = useDialogStyles();
  const dangerButtonSx = useDangerButtonSx();
  const isDark = theme.palette.mode === 'dark';
  const danger = theme.palette.error.main;

  const close = () => { if (!busy) onClose?.(); };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs" PaperProps={{ sx: paperSx }}>
      <DialogContent sx={{ px: 3, pt: 3, pb: 2.5, backgroundColor: theme.palette.background.alt }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* warning badge with a soft red halo */}
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: danger,
              backgroundColor: alpha(danger, 0.14),
              boxShadow: `0 0 0 6px ${alpha(danger, 0.06)}`,
            }}
          >
            <WarningAmberRounded />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
            <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.3 }}>
              {title}
            </Typography>
            {message && (
              <Typography component="div" sx={{ mt: 0.75, fontSize: '0.875rem', lineHeight: 1.55, color: theme.palette.text.secondary }}>
                {message}
              </Typography>
            )}
          </Box>

          <IconButton onClick={close} disabled={busy} aria-label="Close" size="small" sx={{ mt: -0.5, mr: -1, color: theme.palette.text.secondary }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* what is being deleted */}
        {item && (
          <Box
            sx={{
              mt: 2,
              ml: { sm: 7.5 },
              px: 1.5,
              py: 1.1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              borderRadius: 1.5,
              border: `1px solid ${alpha(danger, 0.28)}`,
              borderLeft: `3px solid ${danger}`,
              backgroundColor: alpha(danger, isDark ? 0.08 : 0.05),
            }}
          >
            {ItemIcon && <ItemIcon sx={{ fontSize: 20, color: alpha(danger, 0.9), flexShrink: 0 }} />}
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{item}</Typography>
              {itemDetail && (
                <Typography noWrap sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>{itemDetail}</Typography>
              )}
            </Box>
          </Box>
        )}

        {note && (
          <Box sx={{ mt: 1.5, ml: { sm: 7.5 }, display: 'flex', alignItems: 'center', gap: 0.75, color: theme.palette.text.secondary }}>
            <InfoOutlined sx={{ fontSize: 15 }} />
            <Typography sx={{ fontSize: '0.75rem' }}>{note}</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={actionsSx}>
        <Button onClick={close} disabled={busy} sx={{ ...cancelButtonSx, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : confirmIcon}
          sx={dangerButtonSx}
        >
          {busy ? busyLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
